import React, { useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  ShieldAlert, ShieldCheck, AlertTriangle, CheckCircle, Pencil, Save, X,
  Loader2, Mail, Check, AlertCircle, Search, CheckSquare, XCircle,
  MessageSquare, Send, Flag
} from 'lucide-react';

function getPhaseStatus(pd, mTotal, mUnreviewed, mConfirmed) {
  if (mTotal === 0) return pd.has_ko ? 'ko' : 'passed';
  if (mUnreviewed > 0) return 'missing_info';
  if (mConfirmed > 0) return pd.has_ko ? 'ko' : 'awaiting_broker';
  return pd.has_ko ? 'ko' : 'passed';
}

function StatusIcon({ status }) {
  if (status === 'ko') return <div className="flex items-center gap-1.5 text-red-400"><ShieldAlert className="w-4 h-4" strokeWidth={1.5} /><span className="font-mono text-sm font-medium uppercase">Knock-Out</span></div>;
  if (status === 'passed') return <div className="flex items-center gap-1.5 text-green-400"><ShieldCheck className="w-4 h-4" strokeWidth={1.5} /><span className="font-mono text-sm font-medium uppercase">Passed</span></div>;
  if (status === 'awaiting_broker') return <div className="flex items-center gap-1.5 text-amber-400"><Mail className="w-4 h-4" strokeWidth={1.5} /><span className="font-mono text-sm font-medium uppercase">Awaiting Broker</span></div>;
  return <div className="flex items-center gap-1.5 text-amber-400"><AlertCircle className="w-4 h-4" strokeWidth={1.5} /><span className="font-mono text-sm font-medium uppercase">Missing Info</span></div>;
}

export function ScorecardPanel({
  phase, project, config, dealType, isAcMember, editMode, editedScores, setEditedScores,
  expandedCriteria, setExpandedCriteria, editingNote, setEditingNote,
  noteText, setNoteText, reviewingPhase, foundDialog, setFoundDialog,
  setFoundNote, onStartEdit, onSaveOverrides, onCancelEdit, onSaveNote,
  onToggleBrokerRequested, onSearchForHints, onConfirmMissing, onDraftEmail,
  projectId, onRefresh,
}) {
  const pd = project[phase];
  if (!pd || !config) return null;
  const criteriaList = phase === 'phase1' ? config.phase1 : config.phase2;

  const scoreMap = {};
  pd.scores.forEach(s => { scoreMap[s.criteria_id] = s; });
  const hints = pd.missing_review?.hints || [];
  const hintMap = {};
  hints.forEach(h => { hintMap[h.criteria_id] = h; });

  const missingScores = pd.scores.filter(s => s.data_available === false);
  const mTotal = missingScores.length;
  const mUnreviewed = missingScores.filter(s => !s.review_status || s.review_status === null).length;
  const mFound = missingScores.filter(s => s.review_status === 'found').length;
  const mConfirmed = missingScores.filter(s => s.review_status === 'confirmed_missing').length;
  const phaseStatus = getPhaseStatus(pd, mTotal, mUnreviewed, mConfirmed);
  const isEditing = editMode === phase;
  const colCount = phase === 'phase1' ? 10 : 9;

  return (
    <div className="space-y-4" data-testid={`scorecard-${phase}`}>
      {/* Score Summary */}
      <div className="flex items-center justify-between bg-card border border-border rounded-sm p-4">
        <div className="flex items-center gap-4">
          <div>
            <p className="text-[10px] text-foreground/50 uppercase tracking-wider mb-0.5">Score</p>
            <p className="font-mono text-2xl font-medium text-foreground">{pd.total_score}<span className="text-muted-foreground text-sm">/{pd.max_score}</span></p>
          </div>
          <div className="w-px h-10 bg-border" />
          <div>
            <p className="text-[10px] text-foreground/50 uppercase tracking-wider mb-0.5">Result</p>
            <StatusIcon status={phaseStatus} />
          </div>
          <div className="w-px h-10 bg-border" />
          <div>
            <p className="text-[10px] text-foreground/50 uppercase tracking-wider mb-0.5">Percentage</p>
            <p className="font-mono text-lg font-medium text-foreground">{Math.round((pd.total_score / pd.max_score) * 100)}%</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <Button data-testid={`save-override-${phase}`} onClick={onSaveOverrides} className="bg-green-700 hover:bg-green-600 text-white rounded-sm font-medium text-xs h-8 px-3"><Save className="w-3.5 h-3.5 mr-1" /> Save</Button>
              <Button data-testid={`cancel-override-${phase}`} onClick={onCancelEdit} variant="ghost" className="text-muted-foreground hover:text-foreground rounded-sm text-xs h-8 px-3"><X className="w-3.5 h-3.5 mr-1" /> Cancel</Button>
            </>
          ) : !isAcMember ? (
            <Button data-testid={`edit-scores-${phase}`} onClick={() => onStartEdit(phase)} className="bg-secondary hover:bg-muted text-secondary-foreground rounded-sm font-medium text-xs h-8 px-3"><Pencil className="w-3.5 h-3.5 mr-1" /> Override Scores</Button>
          ) : null}
        </div>
      </div>

      {/* AI Summary */}
      {pd.summary && <div className="bg-card border border-border rounded-sm p-4"><p className="font-heading text-base text-foreground mb-2">AI Assessment</p><p className="text-sm text-foreground/80 leading-relaxed">{pd.summary}</p></div>}

      {/* Score Bar */}
      <div className="bg-card border border-border rounded-sm p-4">
        <div className="h-3 bg-secondary rounded-full overflow-hidden">
          <div className="h-full rounded-full score-bar-animate" style={{
            '--score-width': `${(pd.total_score / pd.max_score) * 100}%`,
            background: phaseStatus === 'ko' ? '#ef4444' : (phaseStatus === 'passed' ? '#22c55e' : '#f59e0b'),
          }} />
        </div>
      </div>

      {/* Scorecard Table */}
      <div className="border border-border rounded-sm overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground font-bold">
              <th className="text-left py-2.5 px-4 w-8">#</th>
              <th className="text-left py-2.5 px-4">Criterion</th>
              {phase === 'phase1' && <th className="text-center py-2.5 px-4 w-14">Wt</th>}
              <th className="text-center py-2.5 px-4 w-16">Score</th>
              <th className="text-center py-2.5 px-4 w-14">K.O.</th>
              <th className="text-center py-2.5 px-4 w-14">Data</th>
              <th className="text-center py-2.5 px-3 w-10" title="Flag Missing Info"><Flag className="w-3 h-3 mx-auto text-muted-foreground/50" /></th>
              <th className="text-center py-2.5 px-3 w-10"><Send className="w-3 h-3 mx-auto" /></th>
              <th className="text-left py-2.5 px-4">Reasoning</th>
              <th className="text-center py-2.5 px-3 w-10"><MessageSquare className="w-3 h-3 mx-auto" /></th>
            </tr>
          </thead>
          <tbody>
            {criteriaList.map((c, i) => (
              <CriteriaRow key={c.id} c={c} i={i} phase={phase} scoreMap={scoreMap} hintMap={hintMap}
                isEditing={isEditing} editedScores={editedScores} setEditedScores={setEditedScores}
                expandedCriteria={expandedCriteria} setExpandedCriteria={setExpandedCriteria}
                editingNote={editingNote} setEditingNote={setEditingNote} noteText={noteText}
                setNoteText={setNoteText} colCount={colCount}
                onSaveNote={onSaveNote} onToggleBrokerRequested={onToggleBrokerRequested}
                projectId={projectId} onRefresh={onRefresh} />
            ))}
          </tbody>
        </table>
      </div>

      {/* Missing Information Section — hide for proprietary deals (no CIM) */}
      {mTotal > 0 && dealType !== 'proprietary' && (
        <MissingInfoSection
          phase={phase} mTotal={mTotal} mUnreviewed={mUnreviewed} mFound={mFound}
          mConfirmed={mConfirmed} hints={hints} scoreMap={scoreMap}
          reviewingPhase={reviewingPhase}
          onSearchForHints={onSearchForHints} onDraftEmail={onDraftEmail}
          onFoundDialog={setFoundDialog} onConfirmMissing={onConfirmMissing}
          setFoundNote={setFoundNote}
        />
      )}
    </div>
  );
}


function StatusBadge({ label, colorClass }) {
  return <span className={`text-[10px] ${colorClass} rounded px-1 font-mono`}>{label}</span>;
}

function CriteriaNameCell({ c, sd, isFound, isMissing, isBrokerResolved }) {
  return (
    <td className="py-2.5 px-4">
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-sm text-foreground">{c.name}</span>
        {sd.manually_overridden && !isBrokerResolved && <StatusBadge label="EDITED" colorClass="text-yellow-500 border border-yellow-800" />}
        {isFound && <StatusBadge label="FOUND IN CIM" colorClass="text-green-400 border border-green-800" />}
        {isMissing && <StatusBadge label="AWAITING BROKER" colorClass="text-red-400 border border-red-800" />}
        {isBrokerResolved && <StatusBadge label="FROM BROKER" colorClass="text-cyan-400 border border-cyan-800" />}
      </div>
    </td>
  );
}

function ScoreEditCell({ isEditing, score, scoreChanged, editEntry, c, isMissing, getScoreColor, setEditedScores }) {
  if (!isEditing) {
    return (
      <td className="py-2.5 px-4 text-center">
        <span className={`font-mono text-sm font-medium ${getScoreColor()}`}>{isMissing ? '?' : score}</span>
      </td>
    );
  }
  const reasonClass = editEntry?.reason?.trim()
    ? 'bg-green-950/30 border border-green-800/30 text-green-300'
    : 'bg-red-950/30 border border-red-800/30 text-red-300 animate-pulse';
  return (
    <td className="py-2.5 px-4 text-center">
      <div className="flex flex-col items-center gap-1">
        <Select value={String(score)} onValueChange={v => setEditedScores(p => ({ ...p, [c.id]: { ...p[c.id], score: parseInt(v) } }))}>
          <SelectTrigger className="w-16 h-7 bg-secondary border-border rounded-sm text-xs font-mono mx-auto" onClick={e => e.stopPropagation()}><SelectValue /></SelectTrigger>
          <SelectContent className="bg-card border-border rounded-sm">{c.options.map(o => <SelectItem key={o.score} value={String(o.score)}>{o.score}</SelectItem>)}</SelectContent>
        </Select>
        {scoreChanged && (
          <input value={editEntry?.reason || ''} onChange={e => { e.stopPropagation(); setEditedScores(p => ({ ...p, [c.id]: { ...p[c.id], reason: e.target.value } })); }}
            onClick={e => e.stopPropagation()} placeholder="Why?"
            className={`w-28 h-5 text-[10px] px-1.5 rounded-sm outline-none ${reasonClass}`} />
        )}
      </div>
    </td>
  );
}


function CriteriaRow({
  c, i, phase, scoreMap, hintMap, isEditing, editedScores, setEditedScores,
  expandedCriteria, setExpandedCriteria, editingNote, setEditingNote,
  noteText, setNoteText, colCount, onSaveNote, onToggleBrokerRequested,
  projectId, onRefresh,
}) {
  const sd = scoreMap[c.id] || {};
  const score = isEditing ? (editedScores[c.id]?.score ?? sd.score ?? 0) : (sd.score ?? 0);
  const editEntry = editedScores[c.id];
  const scoreChanged = isEditing && editEntry && editEntry.score !== (sd.score ?? 0);
  const dataOk = sd.data_available !== false;
  const rSt = sd.review_status;
  const isFound = !dataOk && rSt === 'found';
  const isMissing = !dataOk && rSt === 'confirmed_missing';
  const isBrokerResolved = !dataOk && rSt === 'resolved_broker';
  const isResolved = dataOk || isFound || isBrokerResolved;
  const isKo = c.is_ko && score === 0 && isResolved;
  const isExp = expandedCriteria[`${phase}-${c.id}`];
  const hint = hintMap[c.id];
  const userFlagged = sd.user_flagged_missing === true;

  const toggleExpand = () => setExpandedCriteria(p => ({ ...p, [`${phase}-${c.id}`]: !p[`${phase}-${c.id}`] }));

  const handleFlagMissing = async (e) => {
    e.stopPropagation();
    try {
      await api.flagMissingInfo(projectId, phase, c.id, !userFlagged);
      if (onRefresh) onRefresh();
    } catch { /* silent */ }
  };

  function getScoreColor() {
    if (isMissing) return 'text-amber-400';
    if (score === 3) return 'text-green-400';
    if (score === 2) return 'text-blue-400';
    if (score === 1) return 'text-yellow-400';
    return 'text-red-400';
  }

  function getRowBg() {
    if (isKo) return 'bg-red-950/20';
    if (isMissing) return 'bg-amber-950/10';
    if (!dataOk && !rSt) return 'bg-amber-950/5';
    return 'hover:bg-secondary/30';
  }

  function renderKoCell() {
    if (isKo) return <AlertTriangle className="w-4 h-4 text-red-400 mx-auto" strokeWidth={1.5} />;
    if (c.is_ko) return <CheckCircle className="w-4 h-4 text-green-400/50 mx-auto" strokeWidth={1.5} />;
    return <span className="text-xs text-muted-foreground/30">--</span>;
  }

  function renderDataCell() {
    if (isResolved) return <CheckCircle className="w-3.5 h-3.5 text-green-400/50 mx-auto" strokeWidth={1.5} />;
    if (isMissing) return <XCircle className="w-3.5 h-3.5 text-red-400/60 mx-auto" strokeWidth={1.5} />;
    return <AlertCircle className="w-3.5 h-3.5 text-amber-500 mx-auto" strokeWidth={1.5} />;
  }

  return (
    <React.Fragment>
      <tr data-testid={`criteria-row-${c.id}`} onClick={toggleExpand}
        className={`border-t border-border cursor-pointer transition-colors ${getRowBg()}`}>
        <td className="py-2.5 px-4 text-xs text-muted-foreground font-mono">{i + 1}</td>
        <CriteriaNameCell c={c} sd={sd} isFound={isFound} isMissing={isMissing} isBrokerResolved={isBrokerResolved} />
        {phase === 'phase1' && <td className="py-2.5 px-4 text-center font-mono text-xs text-muted-foreground">{c.weight}x</td>}
        <ScoreEditCell isEditing={isEditing} score={score} scoreChanged={scoreChanged} editEntry={editEntry}
          c={c} isMissing={isMissing} getScoreColor={getScoreColor} setEditedScores={setEditedScores} />
        <td className="py-2.5 px-4 text-center">{renderKoCell()}</td>
        <td className="py-2.5 px-4 text-center">{renderDataCell()}</td>
        <td className="py-2.5 px-3 text-center" onClick={e => e.stopPropagation()}>
          <button data-testid={`flag-missing-${c.id}`} onClick={handleFlagMissing}
            title={userFlagged ? 'Unflag missing info' : 'Flag as missing info'}
            className={`w-5 h-5 rounded-sm border flex items-center justify-center mx-auto transition-colors ${userFlagged ? 'bg-amber-600/20 border-amber-600/40 text-amber-400' : 'border-border text-transparent hover:border-amber-500/50 hover:text-amber-500/50'}`}>
            <Flag className="w-3 h-3" />
          </button>
        </td>
        <td className="py-2.5 px-3 text-center" onClick={e => e.stopPropagation()}>
          <button data-testid={`broker-toggle-${c.id}`} onClick={() => onToggleBrokerRequested(phase, c.id, sd.broker_requested)}
            className={`w-5 h-5 rounded-sm border flex items-center justify-center mx-auto transition-colors ${sd.broker_requested ? 'bg-blue-600/20 border-blue-600/40 text-blue-400' : 'border-border text-transparent hover:border-muted-foreground/50'}`}>
            {sd.broker_requested && <Check className="w-3 h-3" />}
          </button>
        </td>
        <td className="py-2.5 px-4 text-xs text-muted-foreground"><span className="line-clamp-2 leading-relaxed">{sd.reasoning || '--'}</span></td>
        <td className="py-2.5 px-3 text-center">
          <button onClick={(e) => { e.stopPropagation(); setExpandedCriteria(p => ({ ...p, [`${phase}-${c.id}`]: true })); setEditingNote(`${phase}-${c.id}`); setNoteText(''); }}
            className={`mx-auto flex items-center gap-1 justify-center ${(sd.notes_log?.length > 0) ? 'text-blue-400' : 'text-muted-foreground/30 hover:text-muted-foreground'}`}>
            <MessageSquare className="w-3.5 h-3.5" strokeWidth={(sd.notes_log?.length > 0) ? 2 : 1.5} />
            {sd.notes_log?.length > 0 && <span className="text-[10px] font-mono">{sd.notes_log.length}</span>}
          </button>
        </td>
      </tr>
      {isExp && (
        <tr className="border-t border-border/50 bg-secondary/20">
          <td colSpan={colCount} className="py-4 px-6">
            <ExpandedCriteriaDetail c={c} sd={sd} score={score} hint={hint} phase={phase}
              editingNote={editingNote} setEditingNote={setEditingNote}
              noteText={noteText} setNoteText={setNoteText} onSaveNote={onSaveNote} />
          </td>
        </tr>
      )}
    </React.Fragment>
  );
}


function ExpandedCriteriaDetail({ c, sd, score, hint, phase, editingNote, setEditingNote, noteText, setNoteText, onSaveNote }) {
  return (
    <div className="space-y-4 max-w-4xl">
      <div>
        <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2">Scoring Options</p>
        <div className="flex flex-wrap gap-2">
          {c.options.map(o => {
            let optionClass = 'bg-secondary/50 border-border text-muted-foreground';
            if (o.score === score) {
              optionClass = o.ko ? 'bg-red-950/30 border-red-800 text-red-300' : 'bg-blue-950/30 border-blue-800 text-blue-300';
            }
            return (
              <div key={`${o.score}-${o.label}`} className={`text-xs px-2.5 py-1.5 rounded-sm border ${optionClass}`}>
                <span className="font-mono mr-1.5">{o.score}pts</span> {o.label} {o.ko && <span className="ml-1 text-red-500 font-bold">[K.O.]</span>} {o.score === score && <Check className="w-3 h-3 inline ml-1" />}
              </div>
            );
          })}
        </div>
      </div>
      {sd.reasoning && <div><p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5">AI Reasoning</p><p className="text-sm text-foreground/80 leading-relaxed">{sd.reasoning}</p></div>}
      {hint && hint.pages_with_hints?.length > 0 && (
        <div>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5">Document References</p>
          <p className="text-xs text-amber-400 mb-1">Pages: {hint.pages_with_hints.map(p => <span key={p} className="inline-block font-mono bg-amber-900/30 px-1.5 py-0.5 rounded text-amber-300 mr-1">{p}</span>)}</p>
          <p className="text-sm text-muted-foreground leading-relaxed">{hint.found_text}</p>
          {hint.suggestion && <p className="text-sm text-foreground/70 mt-2 leading-relaxed">{hint.suggestion}</p>}
        </div>
      )}
      {/* Notes Thread */}
      <div className="bg-blue-950/10 border border-blue-900/20 rounded-sm px-3 py-2.5">
        <p className="text-xs text-blue-400 font-medium mb-2">Notes ({sd.notes_log?.length || 0})</p>
        {sd.notes_log?.length > 0 && (
          <div className="space-y-2 mb-3">
            {sd.notes_log.map((nl, nli) => (
              <div key={nl.at || `note-${nli}`} className="border-l-2 border-blue-800/30 pl-2.5">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-xs text-blue-300 font-medium">{nl.by}</span>
                  <span className="text-[9px] text-muted-foreground font-mono">{new Date(nl.at).toLocaleString('en-GB', {day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit'})}</span>
                </div>
                <p className="text-xs text-blue-200/80 leading-relaxed">{nl.note}</p>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2" onClick={e => e.stopPropagation()}>
          {editingNote === `${phase}-${c.id}` ? (
            <>
              <input autoFocus value={noteText} onChange={e => setNoteText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && noteText.trim()) onSaveNote(phase, c.id); if (e.key === 'Escape') setEditingNote(null); }}
                placeholder="Add a note..." className="flex-1 bg-secondary border border-border rounded-sm text-xs text-foreground h-7 px-2 outline-none" />
              <Button onClick={() => { if (noteText.trim()) onSaveNote(phase, c.id); }} disabled={!noteText.trim()} className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm text-[11px] h-7 px-2"><MessageSquare className="w-3 h-3 mr-1" /> Post</Button>
              <button onClick={() => setEditingNote(null)} className="text-muted-foreground hover:text-foreground"><X className="w-3.5 h-3.5" /></button>
            </>
          ) : (
            <button onClick={() => { setEditingNote(`${phase}-${c.id}`); setNoteText(''); }}
              className="text-xs text-blue-400/60 hover:text-blue-400 transition-colors">+ Add note</button>
          )}
        </div>
      </div>
      {sd.info_source === 'broker' && (
        <div className="bg-cyan-950/15 border border-cyan-900/20 rounded-sm px-3 py-2.5">
          <p className="text-[10px] text-cyan-400 uppercase tracking-wider mb-1">Scored from Broker Info</p>
          <p className="text-xs text-cyan-300">Score updated with information received from broker{sd.resolved_at ? ` on ${new Date(sd.resolved_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}` : ''}.</p>
        </div>
      )}
      {sd.broker_requested && !sd.info_source && <p className="flex items-center gap-1.5 text-xs text-blue-400"><Send className="w-3 h-3" /> Information requested from broker</p>}
    </div>
  );
}


function MissingInfoSection({ phase, mTotal, mUnreviewed, mFound, mConfirmed, hints, scoreMap, reviewingPhase, onSearchForHints, onDraftEmail, onFoundDialog, onConfirmMissing, setFoundNote }) {
  return (
    <div className="bg-amber-950/15 border border-amber-900/30 rounded-sm p-4 space-y-3" data-testid={`missing-review-${phase}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-amber-500" strokeWidth={1.5} />
          <div>
            <p className="text-sm text-amber-400 font-medium">{mTotal} criteria with insufficient data</p>
            <p className="text-xs text-amber-500/70 mt-0.5">
              {mUnreviewed > 0 && <span>{mUnreviewed} unreviewed</span>}
              {mFound > 0 && <span>{mUnreviewed > 0 ? ' · ' : ''}{mFound} found in document</span>}
              {mConfirmed > 0 && <span>{(mUnreviewed > 0 || mFound > 0) ? ' · ' : ''}{mConfirmed} will be requested from broker</span>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid={`search-hints-${phase}`} onClick={() => onSearchForHints(phase)} disabled={!!reviewingPhase}
            className="bg-amber-700/50 hover:bg-amber-700/70 text-amber-100 border border-amber-700/30 rounded-sm font-medium text-xs h-8 px-3">
            {reviewingPhase === phase
              ? <span className="flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching...</span>
              : <span className="flex items-center gap-1.5"><Search className="w-3.5 h-3.5" /> Search Document for Hints</span>}
          </Button>
          {mConfirmed > 0 && (
            <Button data-testid={`draft-email-${phase}`} onClick={() => onDraftEmail(phase)}
              className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm font-medium text-xs h-8 px-3">
              <Mail className="w-3.5 h-3.5 mr-1" /> Draft Broker Email ({mConfirmed} items)
            </Button>
          )}
        </div>
      </div>
      {hints.length === 0 && mUnreviewed > 0 && (
        <p className="text-xs text-amber-500/60">Click "Search Document for Hints" to check if the CIM has partial info before requesting from the broker.</p>
      )}
      {hints.length > 0 && (
        <div className="space-y-2 mt-3" data-testid={`hint-cards-${phase}`}>
          <p className="text-xs text-muted-foreground/60 mb-2">
            <span className="text-green-400 font-medium">Found in CIM</span> = you can score it yourself (add a note for your team).
            <span className="text-red-400 font-medium ml-2">Still Missing</span> = will be included in the broker email.
          </p>
          {hints.map(hint => {
            const sd2 = scoreMap[hint.criteria_id] || {};
            const rs = sd2.review_status;
            const done = rs === 'found' || rs === 'confirmed_missing';
            return (
              <div key={hint.criteria_id} data-testid={`hint-card-${hint.criteria_id}`}
                className={`border rounded-sm p-3 ${done ? (rs === 'found' ? 'border-green-900/30 bg-green-950/10' : 'border-red-900/30 bg-red-950/10') : 'border-amber-900/30 bg-card'}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <span className="text-sm font-medium text-foreground">{hint.criteria_name}</span>
                      {rs === 'found' && <span className="text-[10px] font-mono text-green-400 border border-green-800 rounded px-1">FOUND — override score</span>}
                      {rs === 'confirmed_missing' && <span className="text-[10px] font-mono text-red-400 border border-red-800 rounded px-1">WILL BE IN BROKER EMAIL</span>}
                    </div>
                    {rs === 'found' && sd2.notes && (
                      <div className="bg-green-950/20 border border-green-900/20 rounded-sm px-2.5 py-1.5 mb-1.5">
                        <p className="text-xs text-green-300"><span className="font-medium">Found by team: </span>{sd2.notes}</p>
                      </div>
                    )}
                    {hint.pages_with_hints?.length > 0 ? (
                      <div className="space-y-1.5">
                        <p className="text-xs text-amber-400">Pages: {hint.pages_with_hints.map(p => <span key={p} className="inline-block font-mono bg-amber-900/30 px-1.5 py-0.5 rounded text-amber-300 mr-1">{p}</span>)}</p>
                        <p className="text-xs text-muted-foreground leading-relaxed whitespace-pre-line">{hint.found_text}</p>
                        <p className="text-xs text-foreground/80 mt-1 leading-relaxed">{hint.suggestion}</p>
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">{hint.suggestion || 'No relevant info found in the document.'}</p>
                    )}
                  </div>
                  {!done && (
                    <div className="flex flex-col gap-1.5 shrink-0">
                      <Button data-testid={`found-btn-${hint.criteria_id}`}
                        onClick={() => { onFoundDialog({ phase, criteriaId: hint.criteria_id, criteriaName: hint.criteria_name }); setFoundNote(''); }}
                        className="bg-green-800/40 hover:bg-green-800/60 text-green-300 border border-green-800/30 rounded-sm text-[11px] h-7 px-2.5">
                        <CheckSquare className="w-3 h-3 mr-1" /> Found in CIM
                      </Button>
                      <Button data-testid={`missing-btn-${hint.criteria_id}`}
                        onClick={() => onConfirmMissing(phase, hint.criteria_id)}
                        className="bg-red-800/30 hover:bg-red-800/50 text-red-300 border border-red-800/30 rounded-sm text-[11px] h-7 px-2.5">
                        <XCircle className="w-3 h-3 mr-1" /> Still Missing
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {mUnreviewed === 0 && mConfirmed > 0 && (
            <div className="bg-blue-950/15 border border-blue-900/20 rounded-sm p-3 mt-2">
              <p className="text-xs text-blue-300">All reviewed. <span className="font-bold">{mConfirmed}</span> item{mConfirmed > 1 ? 's' : ''} will be requested — click <span className="font-medium">"Draft Broker Email"</span> above.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
